import { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import ScheduleDialog, {
    ScheduleDateTimeFields,
    ScheduleErrorMessage,
    ScheduleOptionGroup,
    scheduleDialogButtonClassNames,
} from './crm.opportunity.schedule.dialog';

import useScheduleConfig from '../../../../hooks/crm/useScheduleConfig';

import crmOpportunityScheduleService from '../../../../domain/services/crm/crm.opportunity.schedule.service';
import {
    scheduleTexts,
    scheduleTypeOptions,
    scheduleTypes,
} from '../../../../domain/constant/crm/opportunity/crm.opportunity.schedule.constant';

const ScheduleModal = ({ isOpen, opportunity = {}, activityId = null, origin = null, onClose, onScheduled }) => {

    const [scheduleType, setScheduleType] = useState(scheduleTypes.meeting);
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const { enqueueSnackbar } = useSnackbar();
    const { config, status, reload } = useScheduleConfig(isOpen);

    useEffect(() => {
        if (!isOpen)
            return;

        setScheduleType(scheduleTypes.meeting);
        setDate('');
        setTime('');
        setErrorMessage(null);
    }, [isOpen]);

    const isConfigReady = status === 'ready';
    const canSubmit = isConfigReady && !!date && !!time && !isSubmitting;

    const submit = async () => {

        setIsSubmitting(true);
        setErrorMessage(null);

        try {
            const result = await crmOpportunityScheduleService.schedule(opportunity.id, {
                scheduleType,
                scheduledAt: crmOpportunityScheduleService.toLocalDateTime(date, time),
                concludeActivityId: activityId,
                origin,
            });

            enqueueSnackbar(scheduleTexts.successMessage, { variant: 'success' });
            onScheduled && onScheduled(result);
        } catch (error) {
            setErrorMessage(crmOpportunityScheduleService.getErrorMessage(error, 'Não foi possível agendar o atendimento.'));
        } finally {
            setIsSubmitting(false);
        }
    }

    const renderBody = () => {

        if (status === 'loading')
            return <div className="h-[220px] animate-pulse rounded-lg bg-gray-100" />;

        if (status === 'error')
            return (
                <div className="py-6 text-center text-[13.5px] text-gray-600">
                    {scheduleTexts.loadConfigError}
                    <button type="button" onClick={reload} className="ml-1 font-medium text-primary hover:underline">
                        {scheduleTexts.retryLabel}
                    </button>
                </div>
            );

        return (
            <>
                <ScheduleOptionGroup
                    name="schedule-type"
                    label={scheduleTexts.typeLabel}
                    options={scheduleTypeOptions}
                    value={scheduleType}
                    onChange={setScheduleType}
                    disabled={isSubmitting}
                />

                <ScheduleDateTimeFields
                    idPrefix="schedule"
                    dateLabel={scheduleTexts.dateLabel}
                    timeLabel={scheduleTexts.timeLabel}
                    date={date}
                    time={time}
                    minDate={crmOpportunityScheduleService.getTodayDate()}
                    maxDate={config?.maxDate}
                    onDateChange={setDate}
                    onTimeChange={setTime}
                    disabled={isSubmitting}
                />

                <p className="text-[12px] text-gray-500">
                    {scheduleTexts.getLimitHint(config?.maxDaysAhead, config?.maxPerOpportunity)}
                </p>

                <ScheduleErrorMessage message={errorMessage} />
            </>
        );
    }

    return (
        <ScheduleDialog
            isOpen={isOpen}
            title={scheduleTexts.modalTitle}
            description={scheduleTexts.modalDescription}
            onClose={onClose}
            isBusy={isSubmitting}
            footer={(
                <>
                    <button type="button" onClick={onClose} disabled={isSubmitting} className={scheduleDialogButtonClassNames.secondary}>
                        {scheduleTexts.cancelLabel}
                    </button>
                    <button type="button" onClick={submit} disabled={!canSubmit} className={scheduleDialogButtonClassNames.primary}>
                        {isSubmitting ? scheduleTexts.submittingLabel : scheduleTexts.submitLabel}
                    </button>
                </>
            )}
        >
            {renderBody()}
        </ScheduleDialog>
    );
};

export default ScheduleModal;
